import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Modal from '@/assets/common/Modal/Modal.vue';
import ChemicalPairSplitModal from '@/assets/common/Modal/ChemicalPairSplitModal/ChemicalPairSplitModal.vue';
import { toCamel } from 'snake-camel';
import { WarehouseChemicalWithWPU } from '@/types/Component';

type SplitPair = {
    plantingId: string | null;
    tempPlantingKey: string | null;
    agriFieldId: string;
    label: string;
    area: number | null;
};

type PairShare = {
    plantingId: string | null;
    tempPlantingKey?: string | null;
    agriFieldId: string;
    dilRatio: number | null;
    sprayQty: number | null;
    usageQty: number | null;
    amount: number | null;
};

type ChemicalUsage = {
    id: string | null;
    chemicalId: number | null;
    chemicalName: string | null;
    cropId: string | null;
    appPartBranchNum : number | null;
    usageCountBranchNum : number | null;
    dilRatioBranchNum : number | null;
    sprayQtyBranchNum : number | null;
    warehouseId: string | null;
    warehouseName: string | null;
    workRecordId: string | null;
    workDate: Date | null;
    usageQty: number | null;
    usageUnit: string;
    prevUsageUnit : string;
    amount: number | null;
    sprayQty: number | null;
    sprayUnit: string;
    dilRatio : number | null;
    dilRatioUnit : string;
    unitOfMeasure: number;
    disiDiv: number;
    shares : number | null;
    usedDates: Date[];
    editable: boolean;
    isNotSuitable: boolean;
    isExpired: boolean;
    ingSelConditions: SelCondition[];
    hasCaps: boolean;
    updated: string | null;
    transactions?: Transaction[];
    famicUpdated: string | null;
    cropOutOfScope: boolean;
    pairShares?: PairShare[] | null;
    dilRatioAtSplit?: number | null;
    sprayQtyAtSplit?: number | null;
    processType?: string | null;
};

type SelCondition = {
    basicBranchNum: number;
    ingUsageCntBranchNum: number;
    usageCountOrigin: string | null;
    usageCount: number | null;
    isExpired: boolean;
    chemicalId: number;
    name: string;
}

type ChemicalSettings = {
    caps: ChemAppPart[];
    ingredients: Ingredient[];
}

type ChemAppPart = {
    branchNum: number;
    isNotSuitable: boolean;
    isOutOfScope: boolean;
    cropId: string;
    cropName: string;
    cropOrigin: string;
    al: string | null;
    appPestWeed: string | null;
    purposeUse: string | null;
    whenUse: string | null;
    howUse: string | null;
    fumTime: string | null;
    fumTemp: string | null;
    applSoil: string | null;
    applArea: string | null;
    applChem: string | null;
    usageConditions: UsageCondition[];
    sprayConditions: Condition[];
    dilutionConditions: Condition[];
    ingConditions: IngCondition[];
};

type UsageCondition = {
    branchNum: number;
    value: string;
    count: number;
};

type IngCondition = {
    branchNum: number;
    name: string;
    conditions: DetailIng[];
};

type DetailIng = {
    branchNum: number;
    value: string;
    basicBranchNum: number;
    isExpired: boolean;
    usageCount: number | null;
    name: string;
    chemicalId: number;
};

type Ingredient = {
    branchNum: number;
    name: string;
    isExpired: boolean;
};

type Condition = {
    branchNum: number;
    value: string;
    minValue: number | null;
    maxValue: number | null;
    unit: string | null;
};

type Item = {
    id: string;
    value: string;
    isOutOfScope?: boolean;
};

type Transaction = {
    id: string | null;
    updated: string | null;
};

@Component({
    components: {
        Modal,
        ChemicalPairSplitModal,
    }
})
export default class PesticideUsageModal extends Vue {

    @Prop() 
    showPesticideUsageModal!: boolean

    @Prop() 
    sprayDilRatio!: boolean

    @Prop()
    initialValue!: ChemicalUsage;

    @Prop({ default: false })
    isBulkContext!: boolean;

    @Prop({ default: false })
    isReadOnly!: boolean;

    @Prop({ default: () => [] })
    bulkPairs!: SplitPair[];

    @Prop()
    pdTenantId!: string;

    @Prop()
    cropItemId!: string;

    @Prop() 
    fromWorkRecord!: boolean
    
    @Prop()
    tenantId!: string;

    @Prop()
    cropItemName!: string;
    
    form: ChemicalUsage = this.createEmptyForm();

    isUnknownDate = false;
    altered = false;
    showSplitModal = false;
    private lastSyncedUsageQty: number | null = null;
    private lastSyncedAmount: number | null = null;
    private lastSyncedDilRatio: number | null = null;
    private lastSyncedSprayQty: number | null = null;
    private lastSyncedUnitOfMeasure: number | null = null;
    splitNoticeFields: string[] = [];
    currentKey: 'dil' | 'spray' | 'usage' | null = null;
    chemicalSettings: ChemicalSettings | null = null;
    filteredChemicalSettings: ChemicalSettings | null = null;
    crops: Item[] = [];
    appPestWeeds: Item[] = [];
    selectedAppPestWeed: string | null = null;
    selectedValues: { 
        sp: Condition | null;
        dc: Condition | null;
    } = {
        sp: null,
        dc: null,
    };
    formManual = {
    dil: false,
    spray: false,
    usage: false
    };
    private chemicalUnits = [] as any[];

    private warehouseChemicals = [] as WarehouseChemicalWithWPU[];

    createEmptyForm(): ChemicalUsage {
        return {
            id: null,
            chemicalId: null,
            chemicalName: null,
            cropId: null,
            appPartBranchNum: null,
            usageCountBranchNum : null,
            dilRatioBranchNum: null,
            sprayQtyBranchNum: null,
            warehouseId: null,
            warehouseName: null,
            workRecordId: null,
            workDate: null,
            usageQty: null,
            usageUnit: "kg",
            prevUsageUnit: "kg",
            amount: null,
            sprayQty: null,
            sprayUnit: "L",
            dilRatio: null,
            dilRatioUnit: "倍",
            unitOfMeasure: 0,
            disiDiv: 2,
            shares:null,
            usedDates: [],
            editable: false,
            isNotSuitable: true,
            isExpired: false,
            ingSelConditions: [],
            hasCaps: false,
            updated: null,
            transactions: [],
            famicUpdated: null,
            cropOutOfScope: false,
            pairShares: null,
            dilRatioAtSplit: null,
            sprayQtyAtSplit: null,
            processType: null,
        };
    }
    
    @Watch('showPesticideUsageModal')
    async onModalOpen(val: boolean) {
        this.getChemicalUnits();
        this.getWarehouseChemicals();
        this.crops = [];
        this.appPestWeeds = [];
        this.selectedAppPestWeed = null;
        this.form = this.createEmptyForm();
        this.currentKey = null;
        this.chemicalSettings = null;
        this.filteredChemicalSettings = null;
        this.selectedValues = {
            sp: null,
            dc: null
        };
        if (val) {
            this.$nextTick(() => (this.$refs.disiDivSelect as HTMLSelectElement)?.focus());
            this.form = this.initialValue ? { ...this.initialValue } : this.createEmptyForm();
            this.form.prevUsageUnit = this.form.usageUnit;
            this.isUnknownDate = !this.form.workDate;
            this.altered = false;
            this.splitNoticeFields = [];
            this.lastSyncedUsageQty = this.form.usageQty;
            this.lastSyncedAmount = this.form.amount;
            this.lastSyncedDilRatio = this.form.dilRatio;
            this.lastSyncedSprayQty = this.form.sprayQty;
            this.lastSyncedUnitOfMeasure = this.form.unitOfMeasure;
            if (this.form.chemicalId && this.cropItemId) {
                try {
                    const tenantId = this.tenantId ? this.tenantId : null;
                    const res = await this.$apiClient.doRequest("get", `/admin/chemicals/additional_settings/${this.form.chemicalId}/${this.cropItemId}/${tenantId}`);
                    this.chemicalSettings = toCamel(res) as ChemicalSettings;
                    if (this.chemicalSettings) {
                        const { appPartBranchNum, sprayQtyBranchNum, dilRatioBranchNum } = this.form;
                        this.crops = Array.from(
                            new Map(this.chemicalSettings.caps.map(cap => [cap.cropId, {
                                id: cap.cropId,
                                value: cap.cropName,
                                isOutOfScope: cap.isOutOfScope
                            }])).values()
                        ).sort((a, b) => a.value.localeCompare(b.value, 'ja'));
                        const matchedCap = this.chemicalSettings.caps.find(c => c.branchNum === appPartBranchNum);
                        if (matchedCap) {
                            this.form.cropId = matchedCap.cropId;
                        } else {
                            const nameMatch = this.crops.find(c => c.value === this.cropItemName);
                            const relatedCrop = this.crops.find(c => !c.isOutOfScope);
                            this.form.cropId = nameMatch?.id ?? relatedCrop?.id ?? this.crops[0]?.id ?? null;
                        }
                        if (this.form.cropId) {
                            this.setAppPestWeeds();
                        }
                        this.filteredChemicalSettings = {
                            caps: this.chemicalSettings.caps.filter(cap => {
                                const matchCrop = this.form.cropId ? cap.cropId === this.form.cropId : true;
                                const matchWeed = this.selectedAppPestWeed ? cap.appPestWeed === this.selectedAppPestWeed : true;
                                return matchCrop && matchWeed;
                            }),
                            ingredients: this.chemicalSettings.ingredients ?? []
                        };
                        matchedCap?.ingConditions?.forEach(({ branchNum: ingBranchNum, conditions = [] }) => {
                            const ingSelConditions = this.form.ingSelConditions || [];
                            if (conditions.length === 1 && !ingSelConditions.find(c => c.basicBranchNum === ingBranchNum)) {
                                const d = conditions[0];
                                this.handleIngCondition(ingBranchNum, d.branchNum, d.value, d.usageCount, d.isExpired, d.chemicalId, d.name);
                            }
                        });
                        const dilutionCondition = matchedCap?.dilutionConditions.find(dc => dc.branchNum === dilRatioBranchNum);
                        const sprayCondition = matchedCap?.sprayConditions.find(sc => sc.branchNum === sprayQtyBranchNum);
                        this.selectedValues.dc = dilutionCondition ?? null;
                        this.selectedValues.sp = sprayCondition ?? null;
                        if (!this.form.usageCountBranchNum && matchedCap?.usageConditions?.length === 1) {
                            this.form.usageCountBranchNum = matchedCap.usageConditions[0].branchNum;
                        }
                        if (!dilRatioBranchNum && matchedCap?.dilutionConditions?.length === 1) {
                            const onlyDil = matchedCap.dilutionConditions[0];
                            this.form.dilRatioBranchNum = onlyDil.branchNum;
                            this.selectedValues.dc = onlyDil;
                            this.form.dilRatioUnit = onlyDil.unit ?? '倍';
                        }
                        if (!sprayQtyBranchNum && matchedCap?.sprayConditions?.length === 1) {
                            const onlySpray = matchedCap.sprayConditions[0];
                            this.form.sprayQtyBranchNum = onlySpray.branchNum;
                            this.selectedValues.sp = onlySpray;
                            this.form.sprayUnit = onlySpray.unit ?? 'L';
                        }
                        if (appPartBranchNum == null && this.chemicalSettings?.caps.length === 1) {
                            this.form.appPartBranchNum = this.chemicalSettings.caps[0].branchNum;
                            this.handleAppPart();
                        }
                    }
                } catch {
                    this.$toasted.error('データの取得に失敗しました。');
                }
            }
        }
    }

    get isSelectedCropOutOfScope(): boolean {
        return this.crops.find(c => c.id === this.form.cropId)?.isOutOfScope ?? false;
    }

    handleTab(e: KeyboardEvent) {
        const shift = e.shiftKey;
      
        const disiDivSelect = this.$refs.disiDivSelect as HTMLSelectElement;
        const dilRationInput = this.$refs.dilRationInput as HTMLInputElement;
        const dilRatioUnitInput = this.$refs.dilRatioUnitInput as HTMLInputElement;
        const sprayQtyInput = this.$refs.sprayQtyInput as HTMLInputElement;
        const sprayUnitInput = this.$refs.sprayUnitInput as HTMLInputElement;
        const usageQtyInput = this.$refs.usageQtyInput as HTMLInputElement;
        const usageUnitInput = this.$refs.usageUnitInput as HTMLInputElement;
        const cropInput = this.$refs.cropInput as HTMLInputElement;
        const appPestInput = this.$refs.appPestInput as HTMLInputElement;
        const sharesInput = this.$refs.sharesInput as HTMLInputElement | undefined;
        const amountInput = this.$refs.amountInput as HTMLInputElement;
        const confirmBtn = this.$refs.confirmBtn as HTMLButtonElement;
        const closeBtn = this.$refs.closeBtn as HTMLButtonElement;
        const deleteBtn = this.$refs.deleteBtn as HTMLButtonElement;
      
        const appParts = Array.isArray(this.$refs.appPartBranchNumInput)
          ? this.$refs.appPartBranchNumInput
          : [this.$refs.appPartBranchNumInput].filter(Boolean);
      
        const kisyakys = Array.isArray(this.$refs.kisyakyInput)
          ? this.$refs.kisyakyInput
          : [this.$refs.kisyakyInput].filter(Boolean);

        const usageCnts = Array.isArray(this.$refs.usageConditionInput)
          ? this.$refs.usageConditionInput
          : [this.$refs.usageConditionInput].filter(Boolean);
      
        const sanpus = Array.isArray(this.$refs.sanpuInput)
          ? this.$refs.sanpuInput
          : [this.$refs.sanpuInput].filter(Boolean);
      
        const ingUsages = Array.isArray(this.$refs.ingUsageInput)
          ? this.$refs.ingUsageInput
          : [this.$refs.ingUsageInput].filter(Boolean);
      
        const verticalGroup: HTMLElement[] = [];
        const max = Math.max(appParts.length, kisyakys.length, sanpus.length, usageCnts.length);
      
        for (let i = 0; i < max; i++) {
            const appPart = appParts[i] as HTMLInputElement | undefined;
            const usageCnt = usageCnts[i] as HTMLElement | undefined;
            const kisyaky = kisyakys[i] as HTMLElement | undefined;
            const sanpu = sanpus[i] as HTMLElement | undefined;
          
            if (appPart) {
              verticalGroup.push(appPart);
          
              if (appPart.checked === true) {
                if (usageCnt) verticalGroup.push(usageCnt);
                if (kisyaky) verticalGroup.push(kisyaky);
                if (sanpu) verticalGroup.push(sanpu);
          
                const ingForThisAppPart = ingUsages.filter(
                  (el): el is HTMLInputElement =>
                    el instanceof HTMLInputElement &&
                    typeof el.name === "string" &&
                    el.name.startsWith(`seibun-${String(appPart.value)}-`)
                );
                verticalGroup.push(...ingForThisAppPart);
              }
            }
          }
      
        const focusOrder: HTMLElement[] = [
          disiDivSelect,
          dilRationInput,
          dilRatioUnitInput,
          sprayQtyInput,
          sprayUnitInput,
          usageQtyInput,
          usageUnitInput,
          sharesInput,
          amountInput,
          cropInput,
          appPestInput,
          ...verticalGroup,
          confirmBtn,
          closeBtn,
          deleteBtn,
        ].filter(Boolean) as HTMLElement[];
      
        const currentIndex = focusOrder.indexOf(
          document.activeElement as HTMLElement
        );
        if (currentIndex === -1) return;
      
        if (!shift) {
          if (currentIndex < focusOrder.length - 1) {
            e.preventDefault();
            focusOrder[currentIndex + 1].focus();
          }
        } else {
          if (currentIndex > 0) {
            e.preventDefault();
            focusOrder[currentIndex - 1].focus();
          }
        }
      }    
        
    @Watch('form.workDate')
    onWorkDateChanged(val: Date | null) {
        if (!this.form.warehouseId) {
            if (val) {
                this.isUnknownDate = false;
            } else {
                this.isUnknownDate = true;
            }
        }
    }

    setAppPestWeeds() {
        this.appPestWeeds = Array.from(
            new Set(
                this.chemicalSettings?.caps.filter(cap => cap.cropId === this.form.cropId).map(cap => cap.appPestWeed).filter((v): v is string => !!v)
            )
        ).map(v => ({ id: v, value: v }));
        if (this.appPestWeeds.length === 1) {
            this.selectedAppPestWeed = this.appPestWeeds[0].id;
        }
    }

    alteredForm() {
        this.altered = true;
    }

    onToggleUnknownDate() {
        if (this.isUnknownDate) {
            this.form.workDate = null;
        }
        this.alteredForm();
    }

    async save(flag: boolean): Promise<void> {
        if (flag) {
            const errors: string[] = [];
            const softErrors: string[] = [];
            const regex = /^\d{1,7}(\.\d{1,2})?$/;
            const re = /^\d{1,10}$/;
            const {
                id, chemicalId, workRecordId, workDate, usageUnit, unitOfMeasure, warehouseId, chemicalName, warehouseName,
                sprayQty, sprayUnit, dilRatio, dilRatioUnit, disiDiv, shares, hasCaps,
                appPartBranchNum, usageCountBranchNum, dilRatioBranchNum, sprayQtyBranchNum, isNotSuitable, isExpired, ingSelConditions, amount, updated, transactions, processType
            } = this.form;
            let { usageQty } = this.form;

            if (warehouseName !== "未管理" && !workDate) {
                errors.push('使用日を入力してください。');
            }
            if (workDate && this.form.usedDates.some(date => new Date(date).toDateString() === new Date(workDate).toDateString())) {
                errors.push('既に同じ使用日で農薬の登録があります。別の日付を選択してください。');
            }

            const isValidDisiDiv = disiDiv === 1 || disiDiv === 2 || disiDiv === 3;
            if (!isValidDisiDiv) {
                errors.push('消毒を選択してください。');
            }

            const usageQtyStr = usageQty != null ? usageQty.toString().trim() : '';
            const usageQtyNum = Number(usageQtyStr);
            const isUsageQtyFilled = usageQtyStr !== '';
            const isUsageUnitFilled = usageUnit != null && usageUnit !== '';
            if (warehouseName !== "未管理" && !isUsageQtyFilled) {
                errors.push('使用量を入力してください。');
            }
            if (!isUsageQtyFilled) {
                usageQty = null;
            }
            if (isUsageQtyFilled && (!regex.test(usageQtyStr) || usageQtyNum <= 0 || isNaN(usageQtyNum))) {
                errors.push('使用量は0より大きい整数部7桁、小数部2桁までの数値を入力してください。');
            }
            if (!isUsageUnitFilled) {
                errors.push('使用量の単位を入力してください。');
            }

            const amountStr = amount != null ? amount.toString().trim() : '';
            const isAmountFilled = amountStr !== '';
            if (isAmountFilled && !re.test(amountStr)) {
                errors.push('金額は10桁以内で入力してください。');
            }

            const sharesStr = shares != null ? shares.toString().trim() : '';
            if (sharesStr !== '' && !re.test(sharesStr)) {
                errors.push('株数は10桁以内で入力してください。');
            }

            if (this.sprayDilRatio && this.chemicalSettings && this.chemicalSettings.caps.length > 0 && !this.form.appPartBranchNum) {
                errors.push('適用部条件を選択してください。');
            }

            if (this.sprayDilRatio && this.chemicalSettings && this.chemicalSettings.caps.length > 0 && this.form.appPartBranchNum != null) {
                const matchedCap = this.chemicalSettings?.caps.find(c => c.branchNum === this.form.appPartBranchNum);
                if (matchedCap && matchedCap.usageConditions.length > 0 && !this.form.usageCountBranchNum) {
                    errors.push('使用回数条件を選択してください。');
                }
                if (matchedCap && matchedCap.dilutionConditions.length > 0 && !this.form.dilRatioBranchNum) {
                    errors.push('希釈条件を選択してください。');
                }
                if (matchedCap && matchedCap.sprayConditions.length > 0 && !this.form.sprayQtyBranchNum) {
                    errors.push('散布条件を選択してください。');
                }
                if (matchedCap && (matchedCap.ingConditions?.length || 0) > 0) {
                    const ingSelConditions = this.form.ingSelConditions || [];
                    const hasMissing = matchedCap.ingConditions.some((ing) => {
                        if (!ing.conditions || ing.conditions.length === 0) return false;
                        const selected = ingSelConditions.find((sel) => sel.basicBranchNum === ing.branchNum);
                        if (!selected) return true;
                        const match = ing.conditions?.some((detail) =>
                            detail.branchNum === selected.ingUsageCntBranchNum &&
                            detail.chemicalId === selected.chemicalId
                        );
                        return !match || selected.ingUsageCntBranchNum == null;
                    });
                    if (hasMissing) {
                        errors.push('成分条件を選択してください。');
                    }
                }
            }

            if (this.isDilutionAccessible) {
                const dilRatioStr = dilRatio != null ? dilRatio.toString().trim() : '';
                const dilRatioNum = Number(dilRatioStr);
                const isDilRatioFilled = dilRatioStr !== '';
                const isDilRatioUnitFilled = dilRatioUnit != null && dilRatioUnit !== '';
                if (!isDilRatioFilled) {
                    errors.push('希釈倍数を入力してください。');
                } else {
                    if (!regex.test(dilRatioStr) || dilRatioNum <= 0 || isNaN(dilRatioNum)) {
                        errors.push('希釈倍数は、0より大きい整数部7桁、小数部2桁までの数値を入力してください。');
                    }
                }
                if (!isDilRatioUnitFilled) {
                    errors.push('希釈倍数の単位を入力してください。');
                }
            }
            if (this.isSprayAccessible) {
                const sprayQtyStr = sprayQty != null ? sprayQty.toString().trim() : '';
                const sprayQtyNum = Number(sprayQtyStr);
                const isSprayQtyFilled = sprayQtyStr !== '';
                const isSprayUnitFilled = sprayUnit != null && sprayUnit !== '';
                if (!isSprayQtyFilled) {
                    errors.push('散布量を入力してください。');
                } else {
                    if (!regex.test(sprayQtyStr) || sprayQtyNum <= 0 || isNaN(sprayQtyNum)) {
                        errors.push('散布量は、0より大きい整数部7桁、小数部2桁までの数値を入力してください。');
                    }
                }
                if (!isSprayUnitFilled) {
                    errors.push('散布量の単位を入力してください。');
                }
            }

            if (this.isSelectedCropOutOfScope) {
                softErrors.push('適用条件に利用不可の作物が選択されています。');
            }
            softErrors.push(...this.rangeWarnings);

            if (errors.length === 0 && this.bulkPairs.length >= 2) {
                const shares = this.form.pairShares;
                if (!shares || shares.length === 0 || !this.pairSetMatchesShares(shares)) {
                    errors.push('入力した値と明細の値が一致しません。明細を開いて再設定してください。');
                } else {
                    const isRate = this.form.unitOfMeasure === 1;
                    if (!this.isPoolFieldConsistent(shares, usageQty, 'usageQty', isRate, 2)) {
                        errors.push(isRate
                            ? '使用量が明細の値と一致しません。使用量を再入力してください。'
                            : '使用量が明細の値と一致しません。明細を開いて再設定してください。');
                    }
                    if (!this.isPoolFieldConsistent(shares, amount, 'amount', false, 0)) {
                        errors.push('金額が明細の値と一致しません。明細を開いて再設定してください。');
                    }
                    if (this.isDilutionAccessible && !this.isPoolFieldConsistent(shares, dilRatio, 'dilRatio', true, 2)) {
                        errors.push('希釈倍数が明細の値と一致しません。希釈倍数を再入力してください。');
                    }
                    if (this.isSprayAccessible && !this.isPoolFieldConsistent(shares, sprayQty, 'sprayQty', true, 2)) {
                        errors.push('散布量が明細の値と一致しません。散布量を再入力してください。');
                    }
                }
            }

            if (errors.length > 0 || softErrors.length > 0) {
                const message = [...errors, ...softErrors].join('\n');
                if (errors.length > 0) {
                    window.alert(message);
                    return;
                }
                if (softErrors.length > 0) {
                    if (!window.confirm(message + '\nこのまま保存します。よろしいですか？')) {
                        return;
                    }
                }
            }

            if (this.pdTenantId && workRecordId && workDate && this.initialValue.workDate && workDate != this.initialValue.workDate) {
                const res = await this.$apiClient.doRequest('get', `/work_records/check_overlapping_workers/${this.pdTenantId}/${workRecordId}/${workDate}`);
                if (res) {
                    window.alert(`指定した使用日(${this.formatDate(workDate)})に、作業者が登録された作業記録が存在する為、変更できません。`);
                    return;
                }
            }

            this.$emit('update:showPesticideUsageModal', false);
            const matchedCap = this.chemicalSettings?.caps.find(c => c.branchNum === appPartBranchNum);
            const matchedUsageCondition = matchedCap?.usageConditions.find(u => u.branchNum === usageCountBranchNum);
            const matchedDil = matchedCap?.dilutionConditions.find(d => d.branchNum === dilRatioBranchNum);
            const matchedSpray = matchedCap?.sprayConditions.find(s => s.branchNum === sprayQtyBranchNum);
            const pairShares = this.form.pairShares;
            const dilRatioAtSplit = this.form.dilRatioAtSplit;
            const sprayQtyAtSplit = this.form.sprayQtyAtSplit;
            this.$emit('save', {
                id,
                chemicalId,
                chemicalName,
                workRecordId,
                workDate,
                usageQty,
                usageUnit,
                unitOfMeasure,
                warehouseId,
                warehouseName,
                cropId: matchedCap?.cropId ?? null,
                cropName: matchedCap?.cropOrigin ?? null,
                appPartBranchNum,
                usageCountBranchNum,
                dilRatioBranchNum,
                sprayQtyBranchNum,
                sprayQty,
                sprayUnit,
                dilRatio,
                dilRatioUnit,
                disiDiv,
                shares,
                dilRatioOrigin: matchedDil?.value ?? null,
                sprayQtyOrigin: matchedSpray?.value ?? null,
                dilRatioMin: matchedDil?.minValue ?? null,
                dilRatioMax: matchedDil?.maxValue ?? null,
                sprayQtyMin: matchedSpray?.minValue ?? null,
                sprayQtyMax: matchedSpray?.maxValue ?? null,
                conditionsNotSelected: !this.allConditionsSelected,
                al: matchedCap?.al ?? null,
                appPestWeed: matchedCap?.appPestWeed ?? null,
                purposeUse: matchedCap?.purposeUse ?? null,
                whenUse: matchedCap?.whenUse ?? null,
                howUse: matchedCap?.howUse ?? null,
                fumTime: matchedCap?.fumTime ?? null,
                fumTemp: matchedCap?.fumTemp ?? null,
                applSoil: matchedCap?.applSoil ?? null,
                applArea: matchedCap?.applArea ?? null,
                applChem: matchedCap?.applChem ?? null,
                usageCountOrigin: matchedUsageCondition?.value ?? null,
                usageCount: matchedUsageCondition?.count ?? null,
                isNotSuitable : isNotSuitable ? isNotSuitable : matchedCap?.isNotSuitable || false,
                isExpired,
                ingSelConditions,
                ingredients: this.chemicalSettings?.ingredients ?? [],
                amount,
                hasCaps,
                updated,
                transactions,
                famicUpdated: this.initialValue.famicUpdated ?? null,
                cropOutOfScope: this.isSelectedCropOutOfScope,
                pairShares,
                dilRatioAtSplit,
                sprayQtyAtSplit,
                processType,
            });
        } else {
            if (!window.confirm('削除しますか？')) return;
            this.$emit('update:showPesticideUsageModal', false);
            this.$emit('save', null);
        }
        this.formManual.dil = false
        this.formManual.spray = false
        this.formManual.usage = false
    }

    close() {
        if (!this.altered || window.confirm("変更内容を破棄します。よろしいですか？")) {
            this.form = this.createEmptyForm();
            this.altered = false;
            this.isUnknownDate = false;
            this.formManual.spray = false;
            this.formManual.dil = false;
            this.formManual.usage = false;
            this.crops = [];
            this.appPestWeeds = [];
            this.selectedAppPestWeed = null;
            this.warehouseChemicals = [];
            this.$emit('update:showPesticideUsageModal', false);
        }
    }

    formatDate(date: Date): string {
        const dt = new Date(date);
        const pad = (n: number) => n.toString().padStart(2, '0');
        return `${dt.getFullYear()}/${pad(dt.getMonth() + 1)}/${pad(dt.getDate())}`;
    }

    formatNumberWithCommas = (v: number) => (v % 1 ? v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v.toLocaleString());

    isSameUnitLevel(unitA: string, unitB: string): boolean {
        const heavy = ['kg', 'L'];
        const light = ['g', 'mL', 'cc'];
        return (heavy.includes(unitA) && heavy.includes(unitB)) ||
            (light.includes(unitA) && light.includes(unitB));
    }

    convertToKgL(value: number, unit: string): number {
        const chemUnit = this.getUnit(unit);
        if (!chemUnit || !chemUnit.weight_per_unit) {
            return value;
        }
        return value * chemUnit.weight_per_unit;
    }

    convertFromKgL(value: number, unit: string): number {
        const chemUnit = this.getUnit(unit);
        if (!chemUnit || !chemUnit.weight_per_unit) {
            return value;
        }
        return value / chemUnit.weight_per_unit;
    }

    getUnit(unit: string) {
       return this.chemicalUnits?.find(u => u.name === unit);
    }

    autoCalculation(key: 'dil' | 'spray' | 'usage') {
        this.alteredForm();

        if (this.form.dilRatioUnit !== '倍') return;
        if (!['mL', 'L', 'cc'].includes(this.form.sprayUnit)) return;

        const dil = Number(this.form.dilRatio) || null;
        const sprayQty = Number(this.form.sprayQty) || null;
        const usageQty = Number(this.form.usageQty) || null;
        const sprayUnit = this.form.sprayUnit;
        const usageUnit = this.form.usageUnit;
        const sameUnit = sprayUnit && usageUnit && this.isSameUnitLevel(sprayUnit, usageUnit);

        const values = { dil, spray: sprayQty, usage: usageQty };
        if (key === 'dil' && !dil) {
            this.formManual.dil = false;
            return;
        }
        if (key === 'spray' && !sprayQty) {
            this.formManual.spray = false;
            return;
        }
        if (key === 'usage' && !usageQty) {
            this.formManual.usage = false;
            return;
        }
        const filledKeys = Object.entries(values)
            .filter(([, v]) => v != null)
            .map(([k]) => k as 'dil' | 'spray' | 'usage');

        if ((filledKeys.length === 3 && !this.currentKey) ||
            (filledKeys.length === 2 && !this.currentKey && !filledKeys.includes(key))) {
            if (key === 'dil') {
                this.form.sprayQty = null;
                this.form.usageQty = null;
                this.formManual.spray = false;
                this.formManual.usage = false;
            } else if (key === 'spray') {
                this.form.dilRatio = null;
                this.form.usageQty = null;
                this.formManual.dil = false;
                this.formManual.usage = false;
            } else if (key === 'usage') {
                this.form.dilRatio = null;
                this.form.sprayQty = null;
                this.formManual.dil = false;
                this.formManual.spray = false;
            }
            if (!dil && !sprayQty && !usageQty) {
            this.formManual.dil = false;
            this.formManual.spray = false;
            this.formManual.usage = false;
            this.currentKey = null;
            return;
        }
            this.currentKey = key;
            return;
        } 
        if (key === 'dil' && dil && dil > 0 && this.isUsageAccessible && this.isSprayAccessible) {
            if (!this.formManual.spray && usageQty) {
                const result = sameUnit ? usageQty * dil : this.convertFromKgL(this.convertToKgL(usageQty, usageUnit) * dil, sprayUnit);
                this.form.sprayQty = result > 0 ? this.normalizeAutoValue(result) : null;
            }
            if (!this.formManual.usage && sprayQty) {
                const result = sameUnit ? sprayQty / dil : this.convertFromKgL(this.convertToKgL(sprayQty, sprayUnit) / dil, usageUnit);
                this.form.usageQty = result > 0 ? this.normalizeAutoValue(result) : null;
            }
        } 
        else if (key === 'spray' && sprayQty && sprayUnit && usageUnit && this.isUsageAccessible && this.isDilutionAccessible) {
            if (!this.formManual.dil && usageQty) {
                const result = sameUnit ? sprayQty / usageQty : this.convertToKgL(sprayQty, sprayUnit) / this.convertToKgL(usageQty, usageUnit);
                this.form.dilRatio = result > 0 ? this.normalizeAutoValue(result) : null;
            }
            if (!this.formManual.usage && dil && dil > 0) {
                const result = sameUnit ? sprayQty / dil : this.convertFromKgL(this.convertToKgL(sprayQty, sprayUnit) / dil, usageUnit);
                this.form.usageQty = result > 0 ? this.normalizeAutoValue(result) : null;
            }
        } 
        else if (key === 'usage' && usageQty && usageQty > 0 && sprayUnit && usageUnit && this.isSprayAccessible && this.isDilutionAccessible) {
            if (!this.formManual.spray && dil && dil > 0) {
                const result = sameUnit ? usageQty * dil : this.convertFromKgL(this.convertToKgL(usageQty, usageUnit) * dil, sprayUnit);
                this.form.sprayQty = result > 0 ? this.normalizeAutoValue(result) : null;
            }
            if (!this.formManual.dil && sprayQty) {
                const result = sameUnit ? sprayQty / usageQty : this.convertToKgL(sprayQty, sprayUnit) / this.convertToKgL(usageQty, usageUnit);
                this.form.dilRatio = result > 0 ? this.normalizeAutoValue(result) : null;
            }
        }
        this.currentKey = key;
    }

    convertUnit(value: number, fromUnit: string, toUnit: string): number {
        const baseValue = this.convertToKgL(value, fromUnit);
        return this.convertFromKgL(baseValue, toUnit);
    }

    unitChange(key: 'dil' | 'spray' | 'usage') {
        this.alteredForm();
        if (this.form.dilRatioUnit != "倍") return;
        if (!['mL', 'L', 'cc'].includes(this.form.sprayUnit)) return;
        const dil = Number(this.form.dilRatio) || null;
        const spray = Number(this.form.sprayQty) || null;
        const usage = Number(this.form.usageQty) || null;
        const sprayUnit = this.form.sprayUnit;
        const usageUnit = this.form.usageUnit;

        if (!dil || !spray || !usage || !sprayUnit || !usageUnit) {
         this.pesticideUnitChange()
         return;
        }

        const sameUnit = this.isSameUnitLevel(sprayUnit, usageUnit);

        if (key === 'usage' && this.isUsageAccessible) {
            let result = spray / dil;
            if (!sameUnit) result = this.convertUnit(result, sprayUnit, usageUnit);
            this.form.usageQty = this.normalizeAutoValue(result)
        } else if (key === 'spray' && this.isSprayAccessible) {
            let result = usage * dil;
            if (!sameUnit) result = this.convertUnit(result, usageUnit, sprayUnit);
            this.form.sprayQty = this.normalizeAutoValue(result)
        } else if (key === 'dil' && this.isDilutionAccessible) {
            const sprayBase = sameUnit ? spray : this.convertToKgL(spray, sprayUnit);
            const usageBase = sameUnit ? usage : this.convertToKgL(usage, usageUnit);
            const result = usageBase > 0 ? sprayBase / usageBase : null;
            this.form.dilRatio = this.normalizeAutoValue(result)
        }
    }

    handleIngCondition(basicBranchNum: number, ingUsageCntBranchNum: number, usageCountOrigin: string, usageCount: number | null, isExpired: boolean, chemicalId: number, name: string) {
        this.alteredForm();
        const ingSelConditions = this.form.ingSelConditions || [];
        const filtered = ingSelConditions.filter(c => c.basicBranchNum !== basicBranchNum);
        filtered.push({
            basicBranchNum,
            ingUsageCntBranchNum,
            usageCount,
            usageCountOrigin,
            isExpired,
            chemicalId,
            name
        });
        this.form.ingSelConditions = filtered;
    }

    handleAppPart() {
        this.alteredForm();
        this.form.usageCountBranchNum = null;
        this.form.dilRatioBranchNum = null;
        this.form.sprayQtyBranchNum = null;
        this.selectedValues = {
            sp: null,
            dc: null
        };
        this.form.ingSelConditions = [];
        this.form.sprayQty = null;
        this.form.dilRatio = null;
        this.form.sprayUnit = 'L';
        this.form.dilRatioUnit = '倍';
        const cap = this.chemicalSettings?.caps.find(c => c.branchNum === this.form.appPartBranchNum);
        if (cap?.usageConditions?.length === 1) {
            this.form.usageCountBranchNum = cap.usageConditions[0].branchNum;
        }
        if (cap?.dilutionConditions?.length === 1) {
            this.form.dilRatioBranchNum = cap.dilutionConditions[0].branchNum;
            const dilutionCondition = cap?.dilutionConditions.find(dc => dc.branchNum === this.form.dilRatioBranchNum);
            this.selectedValues.dc = dilutionCondition ?? null;
            this.form.dilRatioUnit = dilutionCondition?.unit ?? '倍';
        }
        if (cap?.sprayConditions?.length === 1) {
            this.form.sprayQtyBranchNum = cap.sprayConditions[0].branchNum;
            const sprayCondition = cap?.sprayConditions.find(sc => sc.branchNum === this.form.sprayQtyBranchNum);
            this.selectedValues.sp = sprayCondition ?? null;
            this.form.sprayUnit = sprayCondition?.unit ?? 'L';
        }
        cap?.ingConditions?.forEach(({ branchNum: ingBranchNum, conditions = [] }) => {
            if (conditions.length === 1) {
                const d = conditions[0];
                this.handleIngCondition(ingBranchNum, d.branchNum, d.value, d.usageCount, d.isExpired, d.chemicalId, d.name);
            }
        });
    }

    handleKisyaku(appPartBranchNum: number, dilRatioBranchNum: number) {
        this.alteredForm();
        if (this.form.appPartBranchNum != appPartBranchNum) {
            this.form.usageCountBranchNum = null;
            this.form.dilRatioBranchNum = null;
            this.form.sprayQtyBranchNum = null;
            this.selectedValues = {
                sp: null,
                dc: null
            };
            this.form.ingSelConditions = [];
        }
        this.form.sprayQty = null;
        this.form.dilRatio = null;
        this.form.sprayUnit = 'L';
        this.form.dilRatioUnit = '倍';
        this.form.appPartBranchNum = appPartBranchNum;
        this.form.dilRatioBranchNum = dilRatioBranchNum;
        const cap = this.chemicalSettings?.caps.find(c => c.branchNum === appPartBranchNum);
        if (cap?.usageConditions?.length === 1) {
            this.form.usageCountBranchNum = cap.usageConditions[0].branchNum;
        }
        if (cap?.sprayConditions?.length === 1) {
            this.form.sprayQtyBranchNum = cap.sprayConditions[0].branchNum;
            const sprayCondition = cap?.sprayConditions.find(sc => sc.branchNum === this.form.sprayQtyBranchNum);
            this.selectedValues.sp = sprayCondition ?? null;
            this.form.sprayUnit = sprayCondition?.unit ?? 'L';
        }
        cap?.ingConditions?.forEach(({ branchNum: ingBranchNum, conditions = [] }) => {
            if (conditions.length === 1) {
                const d = conditions[0];
                this.handleIngCondition(ingBranchNum, d.branchNum, d.value, d.usageCount, d.isExpired, d.chemicalId, d.name);
            }
        });
        const dilutionCondition = cap?.dilutionConditions.find(dc => dc.branchNum === dilRatioBranchNum);
        this.selectedValues.dc = dilutionCondition ?? null;
        this.form.dilRatioUnit = dilutionCondition?.unit ?? '倍';
    }

    handleSanpu(appPartBranchNum: number, sprayQtyBranchNum: number) {
        this.alteredForm();
        if (this.form.appPartBranchNum !== appPartBranchNum) {
            this.form.usageCountBranchNum = null;
            this.form.sprayQtyBranchNum = null;
            this.form.dilRatioBranchNum = null;
            this.selectedValues = {
                sp: null,
                dc: null
            };
            this.form.ingSelConditions = [];
        }
        this.form.sprayQty = null;
        this.form.dilRatio = null;
        this.form.sprayUnit = 'L';
        this.form.dilRatioUnit = '倍';
        this.form.appPartBranchNum = appPartBranchNum;
        this.form.sprayQtyBranchNum = sprayQtyBranchNum;
        const cap = this.chemicalSettings?.caps.find(c => c.branchNum === appPartBranchNum);
        if (cap?.usageConditions?.length === 1) {
            this.form.usageCountBranchNum = cap.usageConditions[0].branchNum;
        }
        if (cap?.dilutionConditions?.length === 1) {
            this.form.dilRatioBranchNum = cap.dilutionConditions[0].branchNum;
            const dilutionCondition = cap?.dilutionConditions.find(dc => dc.branchNum === this.form.dilRatioBranchNum);
            this.selectedValues.dc = dilutionCondition ?? null;
            this.form.dilRatioUnit = dilutionCondition?.unit ?? '倍';
        }
        cap?.ingConditions?.forEach(({ branchNum: ingBranchNum, conditions = [] }) => {
            if (conditions.length === 1) {
                const d = conditions[0];
                this.handleIngCondition(ingBranchNum, d.branchNum, d.value, d.usageCount, d.isExpired, d.chemicalId, d.name);
            }
        });
        const sprayCondition = cap?.sprayConditions.find(sc => sc.branchNum === sprayQtyBranchNum);
        this.selectedValues.sp = sprayCondition ?? null;
        this.form.sprayUnit = sprayCondition?.unit ?? 'L';
    }

    handleUsageCondition(appPartBranchNum: number, usageCountBranchNum: number) {
        this.alteredForm();
        if (this.form.appPartBranchNum !== appPartBranchNum) {
            this.form.sprayQtyBranchNum = null;
            this.form.dilRatioBranchNum = null;
            this.form.usageCountBranchNum = null;
            this.selectedValues = {
                sp: null,
                dc: null
            };
            this.form.ingSelConditions = [];
        }
        this.form.sprayQty = null;
        this.form.dilRatio = null;
        this.form.sprayUnit = 'L';
        this.form.dilRatioUnit = '倍';
        this.form.appPartBranchNum = appPartBranchNum;
        this.form.usageCountBranchNum = usageCountBranchNum;
        const cap = this.chemicalSettings?.caps.find(c => c.branchNum === appPartBranchNum);
        if (cap?.dilutionConditions?.length === 1) {
            this.form.dilRatioBranchNum = cap.dilutionConditions[0].branchNum;
            const dilutionCondition = cap?.dilutionConditions.find(dc => dc.branchNum === this.form.dilRatioBranchNum);
            this.selectedValues.dc = dilutionCondition ?? null;
            this.form.dilRatioUnit = dilutionCondition?.unit ?? '倍';
        }
        if (cap?.sprayConditions?.length === 1) {
            this.form.sprayQtyBranchNum = cap.sprayConditions[0].branchNum;
            const sprayCondition = cap?.sprayConditions.find(sc => sc.branchNum === this.form.sprayQtyBranchNum);
            this.selectedValues.sp = sprayCondition ?? null;
            this.form.sprayUnit = sprayCondition?.unit ?? 'L';
        }
        cap?.ingConditions?.forEach(({ branchNum: ingBranchNum, conditions = [] }) => {
            if (conditions.length === 1) {
                const d = conditions[0];
                this.handleIngCondition(ingBranchNum, d.branchNum, d.value, d.usageCount, d.isExpired, d.chemicalId, d.name);
            }
        });
    }

    handleFilter(which: string) {
        this.alteredForm();
        if (which === 'crop') {
            this.selectedAppPestWeed = null;
            this.appPestWeeds = [];
            if (this.form.cropId) {
                this.setAppPestWeeds();
            }
        }
        if (!this.chemicalSettings) {
            this.filteredChemicalSettings = { caps: [], ingredients: [] };
            return;
        }
        this.filteredChemicalSettings = {
            caps: this.chemicalSettings.caps.filter(cap => {
                const matchCrop = this.form.cropId ? cap.cropId === this.form.cropId : true;
                const matchWeed = this.selectedAppPestWeed ? cap.appPestWeed === this.selectedAppPestWeed : true;
                return matchCrop && matchWeed;
            }),
            ingredients: this.chemicalSettings.ingredients ?? []
        };
        if (this.form.appPartBranchNum == null && this.chemicalSettings?.caps.length === 1) {
            this.form.appPartBranchNum = this.chemicalSettings.caps[0].branchNum;
            this.handleAppPart();
        }
    }

    validateNum(field:any) {
        if (field === 'dilRatio') {
            if (this.form.dilRatio === '' as any || this.form.dilRatio === null || this.form.dilRatio === undefined) {
                return;
            }
            const num = Number(this.form.dilRatio);
            this.form.dilRatio = isNaN(num) ? null : num;
        }

        if (field === 'sprayQty') {
            if (this.form.sprayQty === '' as any || this.form.sprayQty === null || this.form.sprayQty === undefined) {
                return;
            }
            const num = Number(this.form.sprayQty);
            this.form.sprayQty = isNaN(num) ? null : num;
        }

        if (field === 'usageQty') {
            if (this.form.usageQty === '' as any || this.form.usageQty === null || this.form.usageQty === undefined) {
                return;
            }
            const num = Number(this.form.usageQty);
            this.form.usageQty = isNaN(num) ? null : num;
        }

        if (field === 'shares') {
            if (this.form.shares === '' as any || this.form.shares === null || this.form.shares === undefined) {
                return;
            }
            const num = Number(this.form.shares);
            this.form.shares = isNaN(num) ? null : num;
        }

        if (field === 'amount') {
            if (this.form.amount === '' as any || this.form.amount === null || this.form.amount === undefined) {
                return;
            }
            const num = Number(this.form.amount);
            this.form.amount = isNaN(num) ? null : num;
        }

    }

    pesticideUnitChange() {
        const item = this.form;
        if (!item.prevUsageUnit) {
            item.prevUsageUnit = item.usageUnit;
            return;
        }
        this.alteredForm();
        if (item.prevUsageUnit === item.usageUnit) return;
        const oldUnit = item.prevUsageUnit;
        const newUnit = item.usageUnit;
        const newValue = this.convertUnit(Number(item.usageQty), oldUnit, newUnit);
        item.usageQty = newValue > 0 ? this.normalizeAutoValue(newValue) : null;
        item.prevUsageUnit = newUnit;
    }

    async getChemicalUnits() {
        const res = await this.$apiClient.doRequest('get','/unit_conversion/chemicals');
        this.chemicalUnits = res.weight_units;
    }

    get isUsageAccessible(): boolean {
        if (!this.sprayDilRatio) return true;
        if (this.chemicalSettings?.caps.length === 0) return true;
        if (this.form.appPartBranchNum == null) return false;
        return true;
    }

    get isDilutionAccessible(): boolean {
        if (!this.sprayDilRatio) return false;
        if (this.chemicalSettings?.caps.length === 0) return true;
        const cap = this.chemicalSettings?.caps.find(c => c.branchNum === this.form.appPartBranchNum);
        return this.allConditionsSelected && !!cap?.dilutionConditions?.length;
    }

    get isSprayAccessible(): boolean {
        if (!this.sprayDilRatio) return false;
        if (this.chemicalSettings?.caps.length === 0) return true;
        const cap = this.chemicalSettings?.caps.find(c => c.branchNum === this.form.appPartBranchNum);
        return this.allConditionsSelected && !!cap?.sprayConditions?.length;
    }

    get isSprayUnitSelectable() {
        return ['mL', 'L', 'cc'].includes(this.form.sprayUnit);
    }

    get isDilUnitSelectable() {
        return ['倍', 'ppm'].includes(this.form.dilRatioUnit);
    }

    truncateText(text: string | null | undefined, maxLength: number): string {
        const safeText = text || '';
        if (safeText.length > maxLength) {
            return safeText.substring(0, maxLength) + '...';
        }
        return safeText;
    }

    normalizeAutoValue = (v: number | null): number | null => v == null || isNaN(v) ? null : Math.round(v * 100) / 100;

    isUnitDisabled(unitName: string, id: string): boolean {
        return !!id && !this.chemicalUnits.some(u => u.name === unitName && u.weight_per_unit !== null);
    }

    getWarehouseChemicals() {
        if (!this.pdTenantId) return;
        this.$apiClient.doRequest('get', `/admin/warehouses/warehouse_chemicals/${this.pdTenantId}`).then((res) => {
            this.warehouseChemicals = res.warehouseChemicals || [];
        })
    }

    filteredChemicalUnits(chemicalId: number | null, warehouseId: string | null): any[] {
        if (!chemicalId) return this.chemicalUnits;
        const wc = this.warehouseChemicals.find(w => w.chemicalId === chemicalId && (w.warehouseId === warehouseId || w.warehouseName === '未管理'));
        if (!wc) return this.chemicalUnits;
        const isWeightNull = wc.weightPerUnit == null;
        return this.chemicalUnits.filter(unit => isWeightNull ? unit.weight_per_unit == null : unit.weight_per_unit != null);
    }

    get rangeWarnings(): string[] {
        const warnings: string[] = [];
        const { dc, sp } = this.selectedValues;
        if (this.isDilutionAccessible && this.form.dilRatio != null && dc && dc.minValue !== null && dc.maxValue !== null &&
            (this.form.dilRatio < dc.minValue || this.form.dilRatio > dc.maxValue)) {
            warnings.push(`希釈倍数は、使用範囲（${dc.minValue}～${dc.maxValue}）外です。`);
        }
        if (this.isSprayAccessible && this.form.sprayQty != null && sp && sp.minValue !== null && sp.maxValue !== null &&
            (this.form.sprayQty < sp.minValue || this.form.sprayQty > sp.maxValue)) {
            warnings.push(`散布量は、使用範囲（${sp.minValue}～${sp.maxValue}）外です。`);
        }
        return warnings;
    }

    get allConditionsSelected(): boolean {
        if (!this.chemicalSettings || this.chemicalSettings.caps.length === 0) return true;
        if (this.form.appPartBranchNum == null) return false;
        const cap = this.chemicalSettings.caps.find(c => c.branchNum === this.form.appPartBranchNum);
        if (!cap) return false;
        if (cap.dilutionConditions?.length && this.form.dilRatioBranchNum == null) return false;
        if (cap.sprayConditions?.length && this.form.sprayQtyBranchNum == null) return false;
        if (cap.usageConditions?.length && this.form.usageCountBranchNum == null) return false;
        if (cap.ingConditions?.length) {
            const ingSelConditions = this.form.ingSelConditions || [];
            const hasMissing = cap.ingConditions.some((ing) => {
                if (!ing.conditions || ing.conditions.length === 0) return false;
                const selected = ingSelConditions.find((sel) => sel.basicBranchNum === ing.branchNum);
                if (!selected) return true;
                const match = ing.conditions.some((detail) =>
                    detail.branchNum === selected.ingUsageCntBranchNum &&
                    detail.chemicalId === selected.chemicalId
                );
                return !match || selected.ingUsageCntBranchNum == null;
            });
            if (hasMissing) return false;
        }
        return true;
    }

    syncPairSharesFromForm() {
        const { usageQty, amount, dilRatio, sprayQty, unitOfMeasure } = this.form;
        if (usageQty === this.lastSyncedUsageQty && amount === this.lastSyncedAmount
            && dilRatio === this.lastSyncedDilRatio && sprayQty === this.lastSyncedSprayQty
            && unitOfMeasure === this.lastSyncedUnitOfMeasure) {
            return;
        }
        if (this.bulkPairs.length >= 2) {
            const result = this.recomputeStaleFields(usageQty, amount, dilRatio, sprayQty);
            this.form.dilRatioAtSplit = dilRatio;
            this.form.sprayQtyAtSplit = sprayQty;
            if (result.changedFields.length > 0) {
                this.form.pairShares = result.shares;
                this.splitNoticeFields = this.mergeSplitNoticeFields(result.changedFields);
            }
        }
        this.lastSyncedUsageQty = usageQty;
        this.lastSyncedAmount = amount;
        this.lastSyncedDilRatio = dilRatio;
        this.lastSyncedSprayQty = sprayQty;
        this.lastSyncedUnitOfMeasure = unitOfMeasure;
    }

    onUnitOfMeasureChange() {
        if (this.bulkPairs.length >= 2) {
            const { usageQty, amount, dilRatio, sprayQty, unitOfMeasure } = this.form;
            const result = this.forceResplitAllFields(usageQty, amount, dilRatio, sprayQty);
            this.form.pairShares = result.shares;
            this.form.dilRatioAtSplit = dilRatio;
            this.form.sprayQtyAtSplit = sprayQty;
            this.lastSyncedUsageQty = usageQty;
            this.lastSyncedAmount = amount;
            this.lastSyncedDilRatio = dilRatio;
            this.lastSyncedSprayQty = sprayQty;
            this.lastSyncedUnitOfMeasure = unitOfMeasure;
            if (result.changedFields.length > 0) {
                this.splitNoticeFields = this.mergeSplitNoticeFields(result.changedFields);
            }
        }
        if (this.isBulkContext) {
            window.alert(this.form.unitOfMeasure === 1
                ? '明細は自動按分されます。'
                : '明細は自動で按分されます。個別に設定が必要な場合は、明細の再設定を行ってください。');
        }
    }

    openSplitModal() {
        this.showSplitModal = true;
    }

    onSplitSave(payload: { shares: PairShare[]; totalUsageQty: number | null; totalAmount: number | null }) {
        this.form.usageQty = payload.totalUsageQty;
        this.form.amount = payload.totalAmount;
        this.form.pairShares = payload.shares;
        this.form.dilRatioAtSplit = this.form.dilRatio;
        this.form.sprayQtyAtSplit = this.form.sprayQty;
        this.lastSyncedUsageQty = payload.totalUsageQty;
        this.lastSyncedAmount = payload.totalAmount;
        this.lastSyncedDilRatio = this.form.dilRatio;
        this.lastSyncedSprayQty = this.form.sprayQty;
        this.lastSyncedUnitOfMeasure = this.form.unitOfMeasure;
        this.splitNoticeFields = [];
        this.alteredForm();
    }

    private pairSetMatchesShares(shares: PairShare[]): boolean {
        const bulkKeys = this.bulkPairs.map(p => this.sharePairKey(p.plantingId, p.tempPlantingKey, p.agriFieldId));
        return shares.length === bulkKeys.length
            && bulkKeys.every(k => shares.some(s => this.sharePairKey(s.plantingId, s.tempPlantingKey, s.agriFieldId) === k));
    }

    private isPoolFieldConsistent(shares: PairShare[], total: number | null, field: 'usageQty' | 'amount' | 'dilRatio' | 'sprayQty', isRate: boolean, decimals: number): boolean {
        if (total == null) return true;
        if (shares.some(s => s[field] == null)) return false;
        const factor = Math.pow(10, decimals);
        return isRate
            ? shares.every(s => Math.round((s[field] ?? 0) * factor) === Math.round(total * factor))
            : Math.round(shares.reduce((sum, s) => sum + (s[field] ?? 0), 0) * factor) === Math.round(total * factor);
    }

    private recomputeStaleFields(usageQty: number | null, amount: number | null, dilRatio: number | null, sprayQty: number | null): { shares: PairShare[]; changedFields: string[] } {
        if (this.bulkPairs.length === 0) return { shares: [], changedFields: [] };
        let shares = this.ensurePairShares();
        const isRate = this.form.unitOfMeasure === 1;
        const changedFields: string[] = [];

        if (this.isDilutionAccessible && !this.isPoolFieldConsistent(shares, dilRatio, 'dilRatio', true, 2)) {
            shares = shares.map(s => ({ ...s, dilRatio }));
            changedFields.push('希釈倍数');
        }
        if (this.isSprayAccessible && !this.isPoolFieldConsistent(shares, sprayQty, 'sprayQty', true, 2)) {
            shares = shares.map(s => ({ ...s, sprayQty }));
            changedFields.push('散布量');
        }
        if (usageQty != null && !this.isPoolFieldConsistent(shares, usageQty, 'usageQty', isRate, 2)) {
            const usageShares = this.splitByArea(usageQty, 2);
            shares = shares.map((s, i) => ({ ...s, usageQty: usageShares[i] }));
            changedFields.push('使用量');
        }
        if (amount != null && !this.isPoolFieldConsistent(shares, amount, 'amount', false, 0)) {
            const amountShares = this.splitAmountByArea(amount, 0);
            shares = shares.map((s, i) => ({ ...s, amount: amountShares[i] }));
            changedFields.push('金額');
        }

        return { shares, changedFields };
    }

    /**
     * Unconditionally re-splits every applicable field for the current mode, regardless of
     * whether pairShares already looked consistent. Used on unitOfMeasure change, since switching
     * mode changes how usageQty should be distributed even when the pool check happens to still pass.
     */
    private forceResplitAllFields(usageQty: number | null, amount: number | null, dilRatio: number | null, sprayQty: number | null): { shares: PairShare[]; changedFields: string[] } {
        if (this.bulkPairs.length === 0) return { shares: [], changedFields: [] };
        let shares = this.ensurePairShares();
        const changedFields: string[] = [];

        if (this.isDilutionAccessible) {
            shares = shares.map(s => ({ ...s, dilRatio }));
            changedFields.push('希釈倍数');
        }
        if (this.isSprayAccessible) {
            shares = shares.map(s => ({ ...s, sprayQty }));
            changedFields.push('散布量');
        }
        const usageShares = this.splitByArea(usageQty, 2);
        shares = shares.map((s, i) => ({ ...s, usageQty: usageShares[i] }));
        changedFields.push('使用量');

        const amountShares = this.splitAmountByArea(amount, 0);
        shares = shares.map((s, i) => ({ ...s, amount: amountShares[i] }));
        changedFields.push('金額');

        return { shares, changedFields };
    }

    private splitByArea(total: number | null, decimals: number): (number | null)[] {
        if (total == null) return this.bulkPairs.map(() => null);
        if (this.form.unitOfMeasure === 1) {
            const factor = Math.pow(10, decimals);
            const rate = Math.round(total * factor) / factor;
            return this.bulkPairs.map(() => rate);
        }
        return this.splitAmountByArea(total, decimals);
    }

    private splitAmountByArea(total: number | null, decimals: number): (number | null)[] {
        if (total == null) return this.bulkPairs.map(() => null);
        const count = this.bulkPairs.length;
        const factor = Math.pow(10, decimals);
        const areas = this.bulkPairs.map(p => p.area);
        const useArea = areas.every(a => a != null && a > 0);
        let totalArea = 0;
        if (useArea) {
            for (const a of areas) totalArea += a as number;
        }
        const shares = this.bulkPairs.map((_, i) => {
            const raw = useArea ? (total * (areas[i] as number)) / totalArea : total / count;
            return Math.floor(raw * factor) / factor;
        });
        const sum = shares.reduce((s, v) => s + v, 0);
        const remainder = Math.round((total - sum) * factor) / factor;
        shares[0] = Math.round((shares[0] + remainder) * factor) / factor;
        return shares;
    }

    private sharePairKey(plantingId: string | null | undefined, tempPlantingKey: string | null | undefined, agriFieldId: string | null | undefined): string {
        return `${plantingId ?? tempPlantingKey ?? ''}_${agriFieldId ?? ''}`;
    }

    get splitNoticeMessage(): string | null {
        if (this.splitNoticeFields.length === 0) return null;
        return `明細（${this.splitNoticeFields.join('、')}）が計算されました。明細を確認してください。`;
    }

    private mergeSplitNoticeFields(newFields: string[]): string[] {
        const order = ['希釈倍数', '散布量', '使用量', '金額'];
        const merged = new Set([...this.splitNoticeFields, ...newFields]);
        return order.filter(f => merged.has(f));
    }

    private ensurePairShares(): PairShare[] {
        const existing = this.form.pairShares ?? [];
        const existingByKey = new Map(existing.map(s => [this.sharePairKey(s.plantingId, s.tempPlantingKey, s.agriFieldId), s]));
        return this.bulkPairs.map(p => {
            const prior = existingByKey.get(this.sharePairKey(p.plantingId, p.tempPlantingKey, p.agriFieldId));
            return {
                plantingId: p.plantingId,
                tempPlantingKey: p.tempPlantingKey,
                agriFieldId: p.agriFieldId,
                dilRatio: prior?.dilRatio ?? null,
                sprayQty: prior?.sprayQty ?? null,
                usageQty: prior?.usageQty ?? null,
                amount: prior?.amount ?? null,
            };
        });
    }

    get dilConditionUnit(): string | null {
        return this.selectedValues.dc?.unit || null;
    }

    get sprayConditionUnit(): string | null {
        return this.selectedValues.sp?.unit || null;
    }
}
